import { useState, useEffect, useCallback, useRef } from 'react';
import {
  loadWidgetsSdk,
  unloadWidgetsSdk,
  getSdkScriptUrl,
  isSdkScriptLoaded,
} from 'utils/widgets-sdk-loader';

export interface UseWidgetsSdkLoaderConfig {
  environment: string; // e.g., 'dev', 'uat', 'productqa', 'prod'
  pilotId: string; // e.g., '10057'
  subdomain?: string; // e.g., 'cx-editor-test1-dev' (extracted from frontend_url)
}

export interface UseWidgetsSdkLoaderOptions {
  autoLoad?: boolean; // Auto-load when config changes (default: true)
  onLoad?: () => void;
  onError?: (error: Error) => void;
  timeout?: number; // milliseconds, default 30000 (30 seconds - SDK may take time to initialize)
  maxRetries?: number; // Maximum number of retry attempts (default: 3)
  retryDelay?: number; // Delay between retries in milliseconds (default: 1000)
}

export interface UseWidgetsSdkLoaderReturn {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
  load: () => Promise<void>;
  unload: () => void;
}

/**
 * React hook for dynamically loading the Bidgely Widgets SDK
 * 
 * @param config - SDK configuration (environment and pilotId) or null to disable loading
 * @param options - Loading options
 * @returns Loading state and control functions
 * 
 * @example
 * ```tsx
 * const { isLoading, isLoaded, error } = useWidgetsSdkLoader(
 *   { environment: 'productqa', pilotId: '10057' },
 *   { autoLoad: true }
 * );
 * ```
 */
export function useWidgetsSdkLoader(
  config: UseWidgetsSdkLoaderConfig | null,
  options: UseWidgetsSdkLoaderOptions = {}
): UseWidgetsSdkLoaderReturn {
  const { autoLoad = true, onLoad, onError, timeout, maxRetries = 3, retryDelay = 1000 } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const configRef = useRef<UseWidgetsSdkLoaderConfig | null>(config);
  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef(false); // Track if a load is in progress to prevent duplicate calls

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      // Optionally unload on unmount - commented out to preserve SDK across route changes
      // unloadWidgetsSdk();
    };
  }, []);

  // Load function with retry logic
  const load = useCallback(async (attemptNumber: number = 1) => {
    const currentConfig = configRef.current;
    if (!currentConfig) {
      console.warn('[useWidgetsSdkLoader] No config provided, skipping load');
      return;
    }

    // Only skip if the bundle for this project (config URL) is already loaded.
    // When project changes, the script URL changes (subdomain/environment), so we must load the new bundle.
    const expectedUrl = getSdkScriptUrl({
      pilotId: currentConfig.pilotId,
      subdomain: currentConfig.subdomain,
      environment: currentConfig.environment,
    });
    if (window.BidgelyWebSdk && isSdkScriptLoaded(expectedUrl)) {
      console.log('[useWidgetsSdkLoader] SDK already available for this project:', expectedUrl);
      if (isMountedRef.current) {
        setIsLoaded(true);
        setIsLoading(false);
        setError(null);
        retryCountRef.current = 0;
        isLoadingRef.current = false;
      }
      return;
    }
    if (window.BidgelyWebSdk && !isSdkScriptLoaded(expectedUrl)) {
      console.log('[useWidgetsSdkLoader] Project changed – reloading bundle for', expectedUrl);
    }

    // Prevent duplicate loading attempts (only on first attempt)
    if (attemptNumber === 1 && isLoadingRef.current) {
      console.log('[useWidgetsSdkLoader] Load already in progress, skipping duplicate call');
      return;
    }

    // Mark as loading (only on first attempt)
    if (attemptNumber === 1) {
      isLoadingRef.current = true;
    }

    if (isMountedRef.current) {
      setIsLoading(true);
      setIsLoaded(false);
      if (attemptNumber === 1) {
        setError(null); // Only clear error on first attempt
      }
    }

    try {
      // loadWidgetsSdk needs pilotId and subdomain
      await loadWidgetsSdk({ 
        pilotId: currentConfig.pilotId,
        subdomain: currentConfig.subdomain,
        environment: currentConfig.environment,
      }, {
        onLoad: () => {
          if (isMountedRef.current) {
            setIsLoaded(true);
            setIsLoading(false);
            setError(null);
            retryCountRef.current = 0; // Reset retry count on success
            isLoadingRef.current = false; // Clear loading flag
          }
          onLoad?.();
        },
        onError: (err) => {
          // Don't call onError callback here - it will be called in catch block after retries exhausted
          // This prevents multiple error callbacks during retry attempts
          console.error(`[useWidgetsSdkLoader] Load attempt ${attemptNumber} failed:`, err);
        },
        timeout,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      
      // Check if we should retry
      if (attemptNumber < maxRetries && isMountedRef.current) {
        const nextAttempt = attemptNumber + 1;
        retryCountRef.current = nextAttempt;
        console.log(`[useWidgetsSdkLoader] Retrying SDK load (attempt ${nextAttempt}/${maxRetries}) after ${retryDelay}ms...`);
        
        // Schedule retry
        retryTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            load(nextAttempt);
          }
        }, retryDelay);
      } else {
        // Max retries reached or component unmounted
        isLoadingRef.current = false; // Clear loading flag
        if (isMountedRef.current) {
          const finalError = attemptNumber >= maxRetries
            ? new Error(`SDK loading failed after ${maxRetries} attempts: ${error.message}`)
            : error;
          setError(finalError);
          setIsLoading(false);
          setIsLoaded(false);
          retryCountRef.current = 0; // Reset for next load attempt
          
          // Only call onError callback on final failure (after all retries exhausted)
          if (attemptNumber >= maxRetries) {
            onError?.(finalError);
          }
        }
      }
    }
  }, [onLoad, onError, timeout, maxRetries, retryDelay, configRef]);

  // Unload function
  const unload = useCallback(() => {
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    isLoadingRef.current = false;
    unloadWidgetsSdk();
    if (isMountedRef.current) {
      setIsLoaded(false);
      setIsLoading(false);
      setError(null);
    }
  }, []);

  // Auto-load when config changes (e.g. project change then navigating to CX Template Editor).
  // Only skip load when the bundle for the current config URL is already in the DOM.
  useEffect(() => {
    if (!autoLoad || !config) {
      return;
    }

    // Reset retry count when config changes
    retryCountRef.current = 0;

    const expectedUrl = getSdkScriptUrl({
      pilotId: config.pilotId,
      subdomain: config.subdomain,
      environment: config.environment,
    });
    const bundleForThisProjectLoaded = window.BidgelyWebSdk && isSdkScriptLoaded(expectedUrl);
    if (bundleForThisProjectLoaded) {
      if (isMountedRef.current) {
        setIsLoaded(true);
        setIsLoading(false);
        setError(null);
      }
      return;
    }

    // Load SDK for the chosen project (replaces previous project's bundle if different URL)
    load(1);

    // Cleanup function
    return () => {
      // Clear any pending retry when config changes
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      // Don't unload on config change, only on unmount
      // This allows SDK to persist when switching between configs
    };
  }, [config, autoLoad, load]);

  // Wrapper for load function that resets retry count
  const loadWithReset = useCallback(() => {
    retryCountRef.current = 0;
    return load(1);
  }, [load]);

  return {
    isLoading,
    isLoaded,
    error,
    load: loadWithReset,
    unload,
  };
}


